import defaultBackground from 'assets/default_explorer-background.png'
import {
  Button,
  Cog6ToothIcon,
  DeleteConfirmButton,
  Drawer,
  DrawerProps,
  Flex,
  Menu,
  TextField,
} from 'components'
import { MediaInput } from 'components/media'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { WelcomeContent } from 'types'
import CopyTemplateButton from './CopyTemplateButton'
import { useTemplate } from './TemplateProvider'

export const MAX_DESCRIPTION_LENGTH = 250

export interface Props extends Omit<DrawerProps, 'children'> {
  open: boolean
  onClose: () => void
}

export default function TemplateManage({ open, onClose }: Props) {
  const navigate = useNavigate()
  const { deleteTemplate, template, isDirty, updateTemplate } = useTemplate()

  const [errorFields, setErrorFields] = useState<string[]>([])

  if (!template) return null

  const {
    title = '',
    description = { en: '' },
    backgroundMedia,
    screensaverMedia,
    registration,
    welcome,
  } = template

  const descriptionLength = description?.en?.length ?? 0
  const isDescriptionError = descriptionLength > MAX_DESCRIPTION_LENGTH

  function handleDelete() {
    deleteTemplate()
    navigate(-1)
  }

  function updateWelcome(body: Partial<WelcomeContent>) {
    updateTemplate({ welcome: { ...template?.welcome, ...body } })
  }

  function addErrorField(field: string) {
    if (!errorFields.includes(field)) setErrorFields([...errorFields, field])
  }

  function removeErrorField(field: string) {
    if (errorFields.includes(field)) setErrorFields(errorFields.filter(f => f !== field))
  }

  return (
    <Drawer
      onClose={onClose}
      open={open}
      header={`${template ? 'Manage' : 'Create'} Template`}
      headerIcon={<Cog6ToothIcon fontSize="20px" />}
      headerActions={
        <Menu
          menuProps={{ positioning: { placement: 'bottom-start' } }}
          variant="ghost"
          buttonLabel="Actions"
          options={[
            {
              type: 'custom',
              label: (
                <DeleteConfirmButton onDelete={handleDelete} width="100%" borderRadius="none" />
              ),
            },
            ...(template
              ? [
                  {
                    type: 'custom' as const,
                    label: <CopyTemplateButton template={template!} disabled={isDirty} />,
                  },
                ]
              : []),
          ]}
        />
      }
      footer={
        <Button variant="solid" palette="primary" onClick={onClose} width="70px">
          Done
        </Button>
      }
    >
      <Flex direction="column" gap={6}>
        <TextField
          label="Title"
          required
          value={title ?? ''}
          placeholder="Enter template title"
          onChange={e => {
            updateTemplate({ title: e.target.value })
            removeErrorField('title')
          }}
          autoFocus
          onBlur={() => !title && addErrorField('title')}
          isError={errorFields.includes('title')}
          helperText={errorFields.includes('title') ? 'Please enter template title' : undefined}
        />

        <TextField
          label="Description"
          isArea
          value={description?.en ?? ''}
          onChange={e => {
            updateTemplate({ description: { en: e.target.value } })
            if (e.target.value.length <= MAX_DESCRIPTION_LENGTH) {
              removeErrorField('description')
            }
          }}
          placeholder="Enter template description"
          isError={isDescriptionError || errorFields.includes('description')}
          helperText={`${descriptionLength}/${MAX_DESCRIPTION_LENGTH} characters${
            isDescriptionError ? ' - Description is too long' : ''
          }`}
          helperTextProps={{ position: 'static' }}
          onBlur={() => isDescriptionError && addErrorField('description')}
        />

        <TextField
          label="Registration Title"
          value={registration?.title?.en ?? ''}
          placeholder="Enter registration title"
          onChange={e =>
            updateTemplate({
              registration: { ...registration, title: { en: e.target.value } },
            })
          }
        />

        <MediaInput
          label="Background Image"
          defaultImage={defaultBackground}
          type="image"
          id={backgroundMedia}
          onSelect={backgroundMedia => updateTemplate({ backgroundMedia })}
          onClear={backgroundMedia ? () => updateTemplate({ backgroundMedia: null }) : undefined}
        />

        <TextField
          label="Screensaver Title"
          value={screensaverMedia?.title?.en ?? ''}
          placeholder="Enter screensaver title"
          onChange={e =>
            updateTemplate({
              screensaverMedia: { ...screensaverMedia, title: { en: e.target.value } },
            })
          }
        />

        <TextField
          label="Screensaver Subtitle"
          value={screensaverMedia?.subtitle?.en ?? ''}
          placeholder="Enter screensaver subtitle"
          onChange={e =>
            updateTemplate({
              screensaverMedia: { ...screensaverMedia, subtitle: { en: e.target.value } },
            })
          }
        />

        <TextField
          label="Screensaver Help Text"
          value={screensaverMedia?.helpText?.en ?? ''}
          placeholder="Enter screensaver help text"
          onChange={e =>
            updateTemplate({
              screensaverMedia: { ...screensaverMedia, helpText: { en: e.target.value } },
            })
          }
        />

        <MediaInput
          label="Screensaver Image"
          defaultImage={defaultBackground}
          type="image"
          id={screensaverMedia?.initialImage ?? backgroundMedia}
          onSelect={initialImage =>
            updateTemplate({ screensaverMedia: { ...screensaverMedia, initialImage } })
          }
          onClear={
            screensaverMedia?.initialImage
              ? () =>
                  updateTemplate({ screensaverMedia: { ...screensaverMedia, initialImage: null } })
              : undefined
          }
        />

        <MediaInput
          label="Screensaver Video"
          type="video"
          id={screensaverMedia?.items?.[0]?.id}
          onSelect={id =>
            updateTemplate({
              screensaverMedia: { ...screensaverMedia, items: [{ id }] },
            })
          }
          onClear={
            screensaverMedia?.items?.[0]?.id
              ? () =>
                  updateTemplate({
                    screensaverMedia: { ...screensaverMedia, items: undefined },
                  })
              : undefined
          }
        />

        <TextField
          label="Trait Selection Welcome"
          value={welcome?.title?.en ?? ''}
          onChange={e => {
            const en = e.target.value
            updateWelcome({ title: en ? { en } : undefined })
          }}
          placeholder="Welcome!"
          inputContainerProps={{
            helpHover: 'Greeting title to user during trait selection.',
          }}
        />

        <TextField
          label="Trait Selection Subtitle"
          isArea
          value={welcome?.subtitle?.en ?? ''}
          onChange={e => {
            const en = e.target.value
            updateWelcome({ subtitle: en ? { en } : undefined })
          }}
          placeholder="Please continue to customize your experience."
          inputContainerProps={{
            helpHover: 'Help text to user when first entering trait selection.',
          }}
        />
      </Flex>
    </Drawer>
  )
}
